import { useCallback, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { AddFactDialog } from './dialogs/add-fact-dialog';
import { ArchiveSelectedButton } from './dialogs/archive-selected';
import { FilterFactsDialog } from './dialogs/filter-facts-dialog';
import { UpdateRelevanceButton } from './dialogs/update-relevance';
import { Permission } from '../../../../../../../../../../backend/src/auth/permissions/permission';
import { RunV2 } from '../../../../../../../../../../backend/src/run/run.entity';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useResolveFactDocIds } from '@/api/mutations/useResolveFactDocIds';
import { useGetCaseDocTypes } from '@/api/queries/useGetCaseDocTypes';
import { useGetCaseIssues } from '@/api/queries/useGetCaseThemes';
import { useUserContext } from '@/Contexts/User';
import { PermissionGate } from '@/hooks/useHasPermission';
import { FactChronologyDropdown } from '@/new/components/fact-chronology-dropdown';
import { Icon } from '@/new/components/icon';
import { ClusterFactsModal } from '@/new/components/labeller/cluster-facts';
import { CreateInconsistencyModal } from '@/new/components/labeller/create-inconsistency';
import { NaturalLanguageSearchBox } from '@/new/components/natural-language-search-bar';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { Button } from '@/new/components/shadcn/button';
import { ButtonGroup } from '@/new/components/shadcn/button-group';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { SubsetChip } from '@/new/components/subset-chip';
import { SubsetDropdown } from '@/new/components/subset-dropdown';
import { useMatterProcessingStore } from '@/new/stores/matter-processing-store';
import { FactFilters as FactFiltersType } from '@/types';

interface FactsEditorToolbarProps {
  globalFilter: string;
  refetchCaseFacts: () => void;
  clearSearch: () => void;
  setGlobalFilter: (value: string) => void;
  onSearchCall: (searchValue?: string, useNaturalLanguage?: boolean) => void;
  goToPage: (value: number) => void;
  activeFilters: FactFiltersType;
  setFilters: (v: FactFiltersType) => void;
  caseRuns: RunV2[];
  onClusterComplete: () => void;
  processingNL: boolean;
  filterCount: number;
  selectedFactIds: Set<string>;
  setSelectedFactIds: (ids: Set<string>) => void;
  totalUnfilteredCount?: number;
}

export const FactsTableHeader = ({
  globalFilter,
  selectedFactIds,
  refetchCaseFacts,
  clearSearch,
  setGlobalFilter,
  onSearchCall,
  goToPage,
  activeFilters,
  setFilters,
  caseRuns,
  onClusterComplete,
  processingNL,
  filterCount,
  setSelectedFactIds,
  totalUnfilteredCount,
}: FactsEditorToolbarProps) => {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) {
    return;
  }

  const { data: issuesWithCounts = [] } = useGetCaseIssues({ matterId, subsetId });
  const { data: docTypes = [] } = useGetCaseDocTypes({ matterId, subsetId });

  // State
  const [factAdderModalIsOpen, setFactAdderModalIsOpen] = useState(false);
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const [clusterModalOpen, setClusterModalOpen] = useState(false);
  const [createInconsistencyModalOpen, setCreateInconsistencyModalOpen] = useState(false);

  const { user } = useUserContext();
  const isLabellingOrg = user?.organisation?.isLabellingOrg ?? false;

  const navigate = useNavigate();
  const resolveFactDocIds = useResolveFactDocIds();
  const isResolvingDocs = resolveFactDocIds.isLoading;

  const handleCreateSubsetFromFacts = useCallback(async () => {
    try {
      const result = await resolveFactDocIds.mutateAsync({
        matterId,
        factIds: Array.from(selectedFactIds),
      });
      if (result.docIdCount === 0) {
        toast.warning('No source documents found for the selected facts');
        return;
      }
      navigate(`/matters/${matterId}/subsets/new?factsQueryKey=${result.queryKey}`);
    } catch {
      toast.error('Failed to resolve documents from selected facts');
    }
  }, [matterId, selectedFactIds, resolveFactDocIds, navigate]);

  const handleNewFactCreated = useCallback(() => {
    trackCustomEvent(TrackingEventsV2.FactAddOnFactsPage, { matterId });
    refetchCaseFacts();
    setFactAdderModalIsOpen(false);
  }, [refetchCaseFacts, matterId]);

  const resultsIncludedLength = selectedFactIds.size;

  const { getProcessingState } = useMatterProcessingStore();

  return (
    <>
      <PageHeader>
        <div className="flex items-center gap-4">
          <PageTitle text="Facts" />
          <SubsetChip subsetId={subsetId} className="mt-0.5" />
        </div>

        <div className="flex items-center gap-2">
          <PermissionGate permission={Permission.ChronologyCreate} uiMode="disabled">
            <FactChronologyDropdown
              matterId={matterId}
              subsetId={subsetId}
              selectedFactIds={selectedFactIds}
              variant="primary"
            />
          </PermissionGate>

          {/* Permissions handled inside subset dropdown component */}
          <SubsetDropdown
            matterId={matterId}
            subsetId={subsetId}
            disabled={selectedFactIds.size === 0}
            isLoading={isResolvingDocs}
            triggerText="Subset from Sources"
            onCreateNew={handleCreateSubsetFromFacts}
            getDocIds={async () => {
              try {
                const result = await resolveFactDocIds.mutateAsync({
                  matterId,
                  factIds: Array.from(selectedFactIds),
                });
                if (result.docIdCount === 0) {
                  toast.warning('No source documents found for the selected facts');
                  return null;
                }
                return result.docIds;
              } catch {
                toast.error('Failed to resolve documents from selected facts');
                return null;
              }
            }}
          />

          {isLabellingOrg && (
            <>
              <Button disabled={resultsIncludedLength < 2} onClick={() => setClusterModalOpen(true)}>
                <Icon className="lucide--group" />
                Create Cluster
              </Button>

              <Button disabled={resultsIncludedLength < 2} onClick={() => setCreateInconsistencyModalOpen(true)}>
                <Icon className="lucide--triangle-alert" />
                Create Inconsistency
              </Button>
            </>
          )}

          <ButtonGroup>
            <PermissionGate permission={Permission.FactUpdate} uiMode="disabled">
              <UpdateRelevanceButton selectedFactIds={selectedFactIds} />
            </PermissionGate>

            <PermissionGate permission={Permission.FactDelete} uiMode="disabled">
              <ArchiveSelectedButton selectedFactIds={selectedFactIds} setSelectedFactIds={setSelectedFactIds} />
            </PermissionGate>
          </ButtonGroup>

          <PermissionGate permission={Permission.FactCreate} uiMode="disabled">
            <Button
              variant="default"
              onClick={() => setFactAdderModalIsOpen(true)}
              disabled={!getProcessingState(matterId)?.hasCompletedRuns}
            >
              <Icon className="lucide--plus" />
              Add Fact
            </Button>
          </PermissionGate>

          <AddFactDialog
            isOpen={factAdderModalIsOpen}
            onOpenChange={setFactAdderModalIsOpen}
            handleNewFactCreated={handleNewFactCreated}
          />
        </div>
      </PageHeader>

      <div className="flex w-full gap-2">
        <NaturalLanguageSearchBox
          value={globalFilter}
          onChange={setGlobalFilter}
          onSearchCall={onSearchCall}
          clearSearch={clearSearch}
          processingNL={processingNL}
          entityType="facts"
        />

        <ButtonGroup>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={activeFilters.disputedFacts ? 'destructive' : 'default'}
                onClick={() => {
                  setFilters({
                    ...activeFilters,
                    disputedFacts: !activeFilters.disputedFacts,
                  });
                  goToPage(0);
                }}
                className={activeFilters.keyFacts ? 'border-r-0!' : ''}
              >
                <Icon className="lucide--triangle-alert" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Toggle disputed Facts</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={activeFilters.keyFacts ? 'warn' : 'default'}
                onClick={() => {
                  setFilters({
                    ...activeFilters,
                    keyFacts: !activeFilters.keyFacts,
                  });
                  goToPage(0);
                }}
                className={activeFilters.keyFacts ? 'z-10 not-hover:border-l!' : ''}
              >
                <Icon className="lucide--key-round" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Toggle key Facts</TooltipContent>
          </Tooltip>
        </ButtonGroup>

        <FilterFactsDialog
          isOpen={filtersModalOpen}
          onOpenChange={setFiltersModalOpen}
          filterCount={filterCount}
          activeFilters={activeFilters}
          allDocumentTypes={docTypes}
          setFilters={setFilters}
          caseRuns={caseRuns}
          caseIssues={issuesWithCounts}
          goToPage={goToPage}
          totalUnfilteredCount={totalUnfilteredCount}
        />
      </div>

      {clusterModalOpen && (
        <ClusterFactsModal
          isOpen={clusterModalOpen}
          onClose={() => setClusterModalOpen(false)}
          matterId={matterId}
          subsetId={subsetId}
          selectedFactIds={selectedFactIds}
          onSuccess={() => {
            setClusterModalOpen(false);
            onClusterComplete();
          }}
        />
      )}
      {createInconsistencyModalOpen && (
        <CreateInconsistencyModal
          isOpen={createInconsistencyModalOpen}
          onClose={() => setCreateInconsistencyModalOpen(false)}
          caseId={matterId}
          onSuccess={() => {
            setCreateInconsistencyModalOpen(false);
            refetchCaseFacts();
          }}
        />
      )}
    </>
  );
};
